import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

import { useUrlSearchParams } from 'approot/shared/logic/search-url-params.hooks';
import {
  EVENT_CATEGORY_CONTENT,
  EVENT_DATA_FOLLOW_ACTION,
  EVENT_TARGET_TYPE_MODAL,
} from '../data-tracker/data-events.constants';
import { pushDataTrackerEvent } from '../data-tracker/data-tracker-push';
import { CloseModalOptions } from './modal.constants';

// To be used in the modal component when we want it to set url parameters
export const useSetModalUrlParams = (name: string, isOpen: boolean) => {
  const navigate = useNavigate();
  const { searchParams, stringifySearchParams } = useUrlSearchParams();

  useEffect(() => {
    const removeModalFromQuery = () => {
      if (searchParams.get('modal') === name) {
        searchParams.delete('modal');
        if (window.location.search.length > 1) {
          navigate({
            search: stringifySearchParams(),
          });
        }
      }
    };

    const urlModal = searchParams.get('modal');

    if (isOpen && urlModal !== name) {
      searchParams.append('modal', name);
      navigate({
        search: stringifySearchParams(),
      });
    } else if (!isOpen && urlModal !== name) {
      removeModalFromQuery();
    }
    return removeModalFromQuery;
    // eslint-disable-next-line
  }, [isOpen, name]);
};

export const useSimpleModalState = (isDefaultVisible: boolean = false) => {
  const [isVisible, setIsVisible] = useState<boolean>(isDefaultVisible);
  const openedAtRef = useRef<number | undefined>(undefined);

  const openModal = useCallback(() => {
    openedAtRef.current = Date.now();
    setIsVisible(true);
  }, []);

  const closeModal = (options?: CloseModalOptions) => {
    setIsVisible(false);
    if (options?.trackAs) {
      pushDataTrackerEvent(
        options.trackingCategory || EVENT_CATEGORY_CONTENT,
        options.trackAs,
        {
          target_type: EVENT_TARGET_TYPE_MODAL,
          target_identifier: options.trackingLabel,
          duration: openedAtRef.current ? Date.now() - openedAtRef.current : 0,
          ...(options.followAction
            ? {
                [EVENT_DATA_FOLLOW_ACTION]: options.followAction,
              }
            : {}),
        }
      );
    }
  };

  return { isVisible, openModal, closeModal };
};

export const useDownloadingModalState = (isLoading: boolean) => {
  const timeout = useRef<number>(undefined);
  const modalHasBeenShownLongEnough = useRef<boolean>(false);
  const modalClosingWasDelayed = useRef<boolean>(false);
  const { isVisible, openModal, closeModal } = useSimpleModalState();

  useEffect(() => {
    if (isLoading) {
      clearTimeout(timeout.current);
      modalHasBeenShownLongEnough.current = false;
      modalClosingWasDelayed.current = false;
      openModal();
      timeout.current = window.setTimeout(() => {
        if (modalClosingWasDelayed.current) {
          closeModal();
        } else {
          modalHasBeenShownLongEnough.current = true;
        }
      }, 3000);
    } else if (isVisible) {
      if (modalHasBeenShownLongEnough.current) {
        closeModal();
      } else {
        modalClosingWasDelayed.current = true;
      }
    }
    // eslint-disable-next-line
  }, [isLoading, isVisible]);

  return {
    isVisible,
    closeModal,
  };
};
